import React from 'react'

import { useForm, type UseFormInput } from '@mantine/form'

import { StrengthChecker, StrengthResult } from './types'

interface UseStrongPasswordFormInput<
  FormValues extends Record<string, unknown>,
> extends UseFormInput<FormValues> {
  passwordField: keyof FormValues
  skipPasswordCheck?: boolean
}

let strengthChecker: StrengthChecker | null = null

const getZxcvbn = async () => {
  const zxcbv = await import('zxcvbn')
  strengthChecker = zxcbv.default
}

const checkStrength = (password: string): StrengthResult => {
  if (strengthChecker) {
    return strengthChecker(password)
  }

  getZxcvbn()
  return {
    score: 3,
    feedback: {
      warning: '',
      suggestions: [],
    },
  }
}

export const useStrongPasswordForm = <
  FormValues extends Record<string, unknown>,
>(
  params: UseStrongPasswordFormInput<FormValues>,
) => {
  const { passwordField, skipPasswordCheck, ...formProps } = params
  const [passwordStrength, setPasswordStrength] =
    React.useState<StrengthResult>(checkStrength(''))

  const form = useForm<FormValues>({
    ...formProps,
    validate: {
      ...(typeof formProps.validate === 'object' ? formProps.validate : {}),
      [passwordField]: (value: string) => {
        if (!skipPasswordCheck) {
          if (value.length === 0) {
            return 'This field is required'
          } else if (passwordStrength.score <= 2) {
            return 'Pick a stronger password'
          }
        }
      },
    } as UseFormInput<FormValues>['validate'],
  })

  const passwordValue = form.values[passwordField]

  React.useEffect(() => {
    if (typeof passwordValue === 'string') {
      // oxlint-disable-next-line react/set-state-in-effect
      setPasswordStrength(checkStrength(passwordValue))
    }
  }, [passwordValue])

  return {
    form,
    passwordStrength,
  }
}
